"use client";

import { useQueryClient } from "@tanstack/react-query";

import { useOpenApiMutation } from "../../../../request/hooks/openApi/useOpenApiMutation";
import { keyCommunityEvent } from "./keyCommunityEvent";

export const useRequestMutationsCommunitiesAdminEvents = () => {
  const queryClient = useQueryClient();

  return {
    mutationCreateDraft: useOpenApiMutation({
      urlBackendPath: "/communities/{community_id}/admin/events/drafts",
      method: "POST",
    }),
    mutationPublishDraft: useOpenApiMutation({
      urlBackendPath:
        "/communities/{community_id}/admin/events/drafts/{encoded_segment_id}/publish",
      method: "PATCH",
      onSuccess: (data, variables) => {
        const communityId = variables.params?.path?.community_id;
        queryClient.invalidateQueries({
          queryKey: [
            "community-draft-event",
            communityId,
            variables.params?.path?.encoded_segment_id,
          ],
        });
        if (communityId !== undefined) {
          queryClient.invalidateQueries({
            queryKey: keyCommunityEvent({
              communityId,
              eventId: data.event_id,
            }),
          });
        }
      },
    }),
    mutationUpdate: useOpenApiMutation({
      urlBackendPath:
        "/communities/{community_id}/admin/events/{encoded_event_occurrence_id}",
      method: "PATCH",
      onSuccess: (data, variables) => {
        const communityId = variables.params?.path?.community_id;
        if (communityId === undefined) return;
        // the event page is keyed by `event_id` (that is the `[eventId]` route
        // segment), and an update can hand back a re-encoded occurrence, so
        // invalidate both the id we sent and the id we got back
        const eventIds = new Set(
          [
            data.event_id,
            variables.params?.path?.encoded_event_occurrence_id,
          ].filter((eventId): eventId is string => Boolean(eventId)),
        );
        eventIds.forEach((eventId) => {
          queryClient.invalidateQueries({
            queryKey: keyCommunityEvent({ communityId, eventId }),
          });
        });
      },
    }),
    mutationDelete: useOpenApiMutation({
      urlBackendPath:
        "/communities/{community_id}/admin/events/{encoded_event_occurrence_id}",
      method: "DELETE",
      onSuccess: (_, variables) => {
        const communityId = variables.params?.path?.community_id;
        if (communityId === undefined) return;
        queryClient.invalidateQueries({
          queryKey: keyCommunityEvent({
            communityId,
            eventId: variables.params?.path?.encoded_event_occurrence_id ?? "",
          }),
        });
      },
    }),
    mutationCancel: useOpenApiMutation({
      urlBackendPath:
        "/communities/{community_id}/admin/events/{encoded_event_occurrence_id}/cancel",
      method: "PATCH",
      onSuccess: (_, variables) => {
        const communityId = variables.params?.path?.community_id;
        if (communityId === undefined) return;
        queryClient.invalidateQueries({
          queryKey: keyCommunityEvent({
            communityId,
            eventId: variables.params?.path?.encoded_event_occurrence_id ?? "",
          }),
        });
      },
    }),
    mutationSendNotifications: useOpenApiMutation({
      urlBackendPath:
        "/communities/{community_id}/admin/events/{encoded_event_occurrence_id}/send-notifications",
      method: "POST",
    }),
  };
};
